/**
 * Estandarización de nombres de cadena (farmacia) provenientes del upstream.
 *
 * El catálogo externo a veces entrega la misma cadena con variantes de formato
 * — distinto casing ("Gpf" vs "GPF"), espacios sobrantes o acentos — y/o como
 * filas separadas en las agrupaciones. Estas utilidades permiten deduplicar y
 * estandarizar por nombre normalizado de forma consistente.
 */

/** Llave canónica para comparar nombres: minúsculas, sin acentos ni espacios sobrantes. */
export function claveCadena(nombre: string): string {
  return nombre
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('es')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

export interface DatoNombrado {
  label: string
  value: number
}

/**
 * Une filas que representan la misma cadena (por nombre normalizado), sumando
 * sus valores. Como etiqueta canónica conserva la variante que más aporta
 * (mayor valor individual), con desempate alfabético. Devuelve el resultado
 * ordenado de mayor a menor valor.
 */
export function mergeCadenasPorNombre(datos: DatoNombrado[]): DatoNombrado[] {
  const grupos = new Map<string, { value: number; label: string; mejor: number }>()
  for (const d of datos) {
    const label = d.label.trim().replace(/\s+/g, ' ')
    const key = claveCadena(label)
    const prev = grupos.get(key)
    if (!prev) {
      grupos.set(key, { value: d.value, label, mejor: d.value })
    } else {
      prev.value += d.value
      if (
        d.value > prev.mejor ||
        (d.value === prev.mejor && label.localeCompare(prev.label, 'es') < 0)
      ) {
        prev.label = label
        prev.mejor = d.value
      }
    }
  }
  return Array.from(grupos.values())
    .map(({ value, label }) => ({ value, label }))
    .sort((a, b) => b.value - a.value)
}
